export const useCountLabel = (count: number, key: string): string => {
  const { locale, t, te } = useI18n();

  if (locale.value === "ar") {
    switch (true) {
      case count === 1:
        return `${t(key, 0)}`;
      case count === 2:
        return `${t(key, 2)}`;
      case count >= 3 && count <= 10:
        return `${count} ${t(key, 1)}`;
      // vue-i18n's default rule caps the form index at 2, so a three-form
      // string gave the dual here ("15 منتجان"). Arabic uses the singular
      // accusative from 11 up, kept in a sibling "<key>_many" message.
      case count > 10:
        return te(`${key}_many`)
          ? `${count} ${t(`${key}_many`)}`
          : `${count} ${t(key, 3)}`;
      default:
        return "";
    }
  } else {
    return count === 1 ? `${count} ${t(key, 0)}` : `${count} ${t(key, 1)}`;
  }
};

// Builds a "bookforme — <title>" head title that is safe against lazy i18n.
// During SSR head render the locale messages may not be loaded yet, in which
// case vue-i18n returns the key itself (e.g. "page.home.page_title"). Detect
// that and use a known fallback so a raw key never reaches a crawler's
// <title> / og:title.
export const seoTitle = (
  t: (key: string) => string,
  key: string,
  fallback: string,
): string => {
  const value = t(key);
  return `bookforme — ${value === key ? fallback : value}`;
};

export const DEFAULT_TIME_SLOT = 5;

export const SuccessToast = (message: string) => {
  const toast = useToast();

  toast.add({
    title: message,
    color: "green",
    icon: "i-approved-success",
  });
};

export const ErrorToast = (message: string) => {
  const toast = useToast();

  toast.add({
    title: message,
    color: "red",
    icon: "i-approved-warning",
  });
};

export const parseAmount = (
  amount: number | string | null | undefined,
): number => {
  if (typeof amount === "number") {
    return amount;
  }
  if (amount === null || amount === undefined || amount === "") {
    return NaN;
  }
  return Number(amount.replace(/,/g, ""));
};

export const allowEnglishAlphanumeric = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  input.value = input.value.replace(/[^A-Za-z0-9]/g, "");
};

/**
 * The HTTP status behind an unknown error, whatever shape it arrived in —
 * an ofetch `FetchError`, a `createError` result, or a raw response.
 *
 * Needed to tell a storefront the API has never heard of (404) from one it
 * could not answer for right now (throttle, 5xx, timeout). Those used to be
 * indistinguishable at the catch site, so every outage was reported to the
 * visitor as "page not found".
 */
export const httpErrorStatus = (error: unknown): number | undefined => {
  const candidate = error as {
    statusCode?: number;
    status?: number;
    response?: { status?: number };
  } | null;

  return (
    candidate?.statusCode ?? candidate?.status ?? candidate?.response?.status
  );
};

/**
 * The canonical shape of a URL path: exactly one leading slash, no repeated
 * slashes inside it, and no trailing full stops.
 *
 * All three used to reach the router verbatim and 404 on storefronts that
 * exist — a doubled slash from a hand-edited link, and a full stop a link
 * detector glued on from the end of the sentence the link sat in, which is
 * easy to do in an Arabic bio where the URL is the last thing on the line.
 *
 * Reducing the leading slashes to one matters on its own, beyond the 404:
 * `//host/path` is read as protocol-relative, so a path that keeps them can
 * turn a redirect into an off-site one.
 */
export const canonicalUrlPath = (path: string): string => {
  const singleSlashed = `/${path
    .replace(/^\/+/, "")
    .replace(/\/{2,}/g, "/")}`;

  // Only a run of dots at the very end goes, so `/x.html` keeps its extension.
  return singleSlashed.replace(/\.+$/, "") || "/";
};

// Support WhatsApp line, shared by the floating button on the landing layout
// and the contact page card so the two can't drift apart.
export const WHATSAPP_NUMBER = "+970 59 708 3596";
export const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, "")}`;
